import { yupResolver } from "@hookform/resolvers/yup";
import { Col, Row } from "antd";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { RootState } from "src";
import { userSchema } from "src/types/userSchema";
import AppRoutes from "./components/Routes";
import { Logo, Sidebar } from "./components/utils";
import { StickyTotal } from "./components/utils/StickyTotal";
import { useMediaQuery } from "./hooks/useMediaQuery";
import { TaskSidebar } from "./components/utils/TaskSidebar";
import { ConfirmationProvider } from "./context/confirmation";
import { UserProvider } from "./context/user";
import { TasksProvider, useTasks } from "./context/tasks";
import { useResizeObserver } from "./hooks/useResizeObserver";
import { useProximityQuote } from "./hooks/useProximityQuote";

function AppContent() {
  const { page } = useSelector((state: RootState) => state.bookingReducer);

  /**
   * Here, at the root, because the question it answers can change on any step
   * and on any screen. It used to sit in the sidebar, which a phone does not
   * show for the first three steps — so a phone customer's bottom bar priced
   * without area pricing until checkout, and a quick Book was refused for it.
   */
  useProximityQuote();

  const width = useResizeObserver();

  const isMobile = useMemo(() => width <= 500, [width]);

  const { pathname } = useLocation();

  const isTasksPage = pathname === "/tasks";

  /**
   * Steps one to three put `MobileNavbar` along the bottom on a phone-sized
   * screen, and it carries the price — so the sidebar stands down for it.
   * Hoisted because the sticky total has to know where that bar already is:
   * two fixed bars on one screen is worse than neither.
   */
  const displayBottomNav = isMobile && page < 4;

  // hide sidebar if the router path is /payments/* or /confirmation
  const hideSidebar = useMemo(
    () =>
      displayBottomNav ||
      pathname.includes("/payments") ||
      pathname.includes("/confirmation") ||
      (isTasksPage && isMobile),
    [displayBottomNav, isMobile, pathname, isTasksPage]
  );

  /**
   * Below antd's `lg` the sidebar column falls to `xs={24}` and the cart stacks
   * under the form, out of sight. Asked of the viewport rather than of
   * `isMobile`, which reads `window.screen.width`: a laptop with a narrow
   * window stacks the cart while still reporting a 1440px screen, and on that
   * machine the price would otherwise be off the bottom of the page with
   * nothing standing in for it.
   */
  const cartIsStacked = useMediaQuery("(max-width: 991.98px)");

  const columnSize = useMemo(() => {
    if (hideSidebar) return 24;
    if (isTasksPage) return 18;
    if (page < 5) {
      return 18;
    }
    if (page === 5) return 12;
    return 24;
  }, [hideSidebar, page, isTasksPage]);

  const methods = useForm({ resolver: yupResolver(userSchema) });

  // Use tasks context only if available (within TasksProvider)
  let selectedTasks: any[] = [];
  try {
    const tasksContext = useTasks();
    selectedTasks = tasksContext.selectedTasks;
  } catch {
    // Not within TasksProvider, that's okay
  }

  const showSidebar = !hideSidebar && (page < 6 || isTasksPage);

  /**
   * Whether the column holds the booking cart rather than the tasks one.
   *
   * Named because the sticky bar mirrors the *cart*, not the column: it reads
   * `bookingReducer`, so on `/tasks` it would quote a booking total for a
   * basket of tasks. Both this and the column's own choice of component read
   * it, so the two cannot drift apart.
   */
  const showCart = showSidebar && !isTasksPage;

  /**
   * The cart is on the page but below it, and nothing else is holding the
   * price. No test for `displayBottomNav` here: it is the first term of
   * `hideSidebar`, so anything that shows a sidebar has already ruled it out.
   */
  const showStickyTotal = showCart && cartIsStacked;

  return (
    <div className={showStickyTotal ? "App App--sticky-total" : "App"}>
      <Row>
        <Col lg={columnSize} xs={24}>
          <div className="content">
            <Logo />
            <br />
            <UserProvider>
              <ConfirmationProvider>
                <FormProvider {...methods}>
                  <AppRoutes />
                </FormProvider>
              </ConfirmationProvider>
            </UserProvider>
          </div>
        </Col>
        {showSidebar ? (
          <Col
            lg={24 - columnSize || 24}
            xs={24}
            style={{
              minHeight: "100%",
              backgroundColor: "#DFF9FF",
            }}
          >
            {showCart ? <Sidebar /> : <TaskSidebar selectedTasks={selectedTasks} />}
          </Col>
        ) : null}
      </Row>
      {showStickyTotal ? <StickyTotal /> : null}
    </div>
  );
}

function App() {
  return (
    <TasksProvider>
      <AppContent />
    </TasksProvider>
  );
}

export default App;
